import { useState, useRef, useEffect, useCallback } from "react";
import { MessageSquare } from "lucide-react";
import ProfileIconLink from "@/components/ProfileIconLink";
import { useEnforceLegalApproval } from "@/hooks/use-enforce-legal";
import { useAuth } from "@/hooks/useAuth";
import { listMessages, sendMessage, type ChatMessage } from "@/integrations/backend/chat";
import { getProfileById } from "@/integrations/backend/profiles";
import { ClientPageLayout } from "@/components/client/ClientPageLayout";
import { ClientChatShell } from "@/components/client/ClientChatShell";
import type { TrainerInfo } from "@/components/client/TrainerChatSidebar";

const ClientChat = () => {
  useEnforceLegalApproval();
  const { profile } = useAuth();
  const [trainerId, setTrainerId] = useState<string | null>(null);
  const [trainer, setTrainer] = useState<TrainerInfo | null>(null);
  const [trainerResolved, setTrainerResolved] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [sending, setSending] = useState(false);
  const [draft, setDraft] = useState("");
  const listRef = useRef<HTMLDivElement | null>(null);
  const isInitialLoadRef = useRef(true);

  useEffect(() => {
    const assignedTrainerId = profile?.assigned_trainer_id || null;
    setTrainerId(assignedTrainerId);
    setTrainerResolved(true);

    if (!assignedTrainerId) {
      setTrainer(null);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const trainerProfile = await getProfileById(assignedTrainerId);
        if (cancelled) return;
        const firstName = trainerProfile.first_name || "";
        const lastName = trainerProfile.last_name || "";
        const trainerName =
          `${firstName} ${lastName}`.trim() || trainerProfile.email || "Trainer";
        const trainerInitials =
          `${firstName[0] || ""}${lastName[0] || ""}`.toUpperCase().slice(0, 2) || "TR";

        setTrainer({
          name: trainerName,
          avatarUrl: trainerProfile.avatar_url,
          initials: trainerInitials,
        });
      } catch (error) {
        console.error("Failed to fetch trainer profile", error);
        if (cancelled) return;
        setTrainer({
          name: "Your Trainer",
          avatarUrl: null,
          initials: "TR",
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [profile?.assigned_trainer_id]);

  const fetchMessages = useCallback(
    async (isRefresh = false) => {
      if (!profile?.id) return;

      try {
        if (isInitialLoadRef.current) {
          setLoading(true);
        } else if (isRefresh) {
          setRefreshing(true);
        }
        const msgs = await listMessages(profile.id);
        setMessages(msgs);
      } catch (error) {
        console.error("Failed to fetch messages", error);
      } finally {
        if (isInitialLoadRef.current) {
          setLoading(false);
          isInitialLoadRef.current = false;
        } else if (isRefresh) {
          setRefreshing(false);
        }
      }
    },
    [profile?.id]
  );

  useEffect(() => {
    if (!profile?.id) return;
    isInitialLoadRef.current = true;
    fetchMessages();
  }, [profile?.id, fetchMessages]);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
  }, [messages.length]);

  const send = async () => {
    const text = draft.trim();
    if (!text || !profile?.id || sending || !trainerId) return;

    try {
      setSending(true);
      await sendMessage({
        profile_id: profile.id,
        content: text,
      });
      setDraft("");
      await fetchMessages(false);
    } catch (error) {
      console.error("Failed to send message", error);
    } finally {
      setSending(false);
    }
  };

  const chatHeader = (
    <>
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <MessageSquare className="w-6 h-6 text-primary" />
          <span>Chat</span>
        </h1>
        <ProfileIconLink />
      </div>
      <p className="text-sm text-gray-light mt-1 hidden sm:block">
        Message your trainer directly
      </p>
    </>
  );

  if (!profile?.id) {
    return (
      <ClientPageLayout
        activeTab="chat"
        fillViewport
        contentClassName="flex flex-col flex-1 min-h-0 p-3 sm:p-4"
        header={chatHeader}
      >
        <div className="flex flex-1 items-center justify-center rounded-2xl border border-primary/20 bg-dark-card p-8 text-center text-white">
          <p>Loading chat…</p>
        </div>
      </ClientPageLayout>
    );
  }

  return (
    <ClientPageLayout
      activeTab="chat"
      fillViewport
      contentClassName="flex flex-col flex-1 min-h-0 p-3 sm:p-4"
      header={chatHeader}
    >
      <ClientChatShell
        trainer={trainer}
        trainerId={trainerId}
        trainerResolved={trainerResolved}
        messages={messages}
        loading={loading}
        refreshing={refreshing}
        sending={sending}
        draft={draft}
        onDraftChange={setDraft}
        onSend={send}
        onRefresh={() => fetchMessages(true)}
        listRef={listRef}
      />
    </ClientPageLayout>
  );
};

export default ClientChat;
